import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { SlidersHorizontal, Loader2, Save, RotateCcw } from "lucide-react";
import Header from "@/components/Header";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { KPI_LABEL_DEFS, KPI_DEFAULT_LABELS } from "@/lib/kpiLabels";
import { toast } from "sonner";

export default function KPISettings() {
  const [labels, setLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setLabels(r.data.kpi_labels || {}))
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const clean = {};
      KPI_LABEL_DEFS.forEach((d) => {
        clean[d.key] = (labels[d.key] ?? "").trim();
      });
      const { data } = await api.put("/settings", { kpi_labels: clean });
      setLabels(data.kpi_labels || {});
      toast.success("Nomes dos KPIs atualizados");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const resetAll = () => {
    const reset = {};
    KPI_LABEL_DEFS.forEach((d) => { reset[d.key] = ""; });
    setLabels(reset);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Definições de KPI</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Personalize o nome de cada caixa de KPI. Os valores calculados mantêm-se iguais — apenas as etiquetas mudam.
          </p>
        </div>

        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <motion.form initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            onSubmit={save} className="rounded-xl border border-border bg-card p-5 space-y-5" data-testid="kpi-settings-form">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="h-5 w-5 text-primary" />
              <h2 className="font-display text-base font-bold tracking-tight">Nomes das caixas</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {KPI_LABEL_DEFS.map((d) => (
                <div key={d.key} className="space-y-2">
                  <Label htmlFor={`kpi-${d.key}`}>
                    <span className="text-xs text-muted-foreground">Padrão: {KPI_DEFAULT_LABELS[d.key]}</span>
                  </Label>
                  <Input id={`kpi-${d.key}`} value={labels[d.key] ?? ""}
                    onChange={(e) => setLabels({ ...labels, [d.key]: e.target.value })}
                    placeholder={KPI_DEFAULT_LABELS[d.key]} data-testid={`kpi-label-input-${d.key}`} />
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center pt-1">
              <Button type="button" variant="outline" onClick={resetAll} className="gap-2" data-testid="kpi-reset-button">
                <RotateCcw className="h-4 w-4" /> Repor padrões
              </Button>
              <Button type="submit" disabled={saving} className="gap-2" data-testid="kpi-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Guardar
              </Button>
            </div>
          </motion.form>
        )}
      </main>
    </div>
  );
}
